import state from "../state/state";
import globals from "../constants/env";
import _ from "lodash";
import { toast } from "react-toastify";

import { postMessage } from "../actions/mobileAppCommunicationActions";
import { isMobile } from "../utils/utils";

const WEB_DIAGNOSTICS_GLOBAL_KEY = "__AMBIFI_WEB_DIAGNOSTICS__";

function getWebDiagnosticsApi() {
	if (typeof window === "undefined") {
		return null;
	}

	return window[WEB_DIAGNOSTICS_GLOBAL_KEY] || null;
}

function recordWebDiagnosticEvent(eventType, detail = {}) {
	try {
		const api = getWebDiagnosticsApi();
		if (api && _.isFunction(api.recordEvent)) {
			return api.recordEvent(eventType, detail);
		}
		if (typeof window !== "undefined" && _.isFunction(window.ambifiRecordDiagnosticEvent)) {
			return window.ambifiRecordDiagnosticEvent(eventType, detail);
		}
	} catch (err) {
		console.warn("Unable to record diagnostic event", err);
	}
	return null;
}

function openWebDiagnostics(reason, options = {}) {
	try {
		const api = getWebDiagnosticsApi();
		if (api && _.isFunction(api.open)) {
			return api.open(reason, options);
		}
		if (typeof window !== "undefined" && _.isFunction(window.ambifiDiagnostics)) {
			return window.ambifiDiagnostics(reason, options);
		}
	} catch (err) {
		console.warn("Unable to open diagnostics", err);
	}
	return null;
}

function sendWebDiagnosticReport(reason, options = {}) {
	try {
		const api = getWebDiagnosticsApi();
		if (api && _.isFunction(api.sendReport)) {
			return api.sendReport(reason, options);
		}
		if (typeof window !== "undefined" && _.isFunction(window.ambifiSendDiagnosticReport)) {
			return window.ambifiSendDiagnosticReport(reason, options);
		}
	} catch (err) {
		return Promise.reject(err);
	}
	return Promise.reject(new Error("AmbiFi diagnostics are not available in this window."));
}

export function showAlert(message, cb) {
	if (message instanceof Error) {
		console.error("receieved an error, expecting an Object", message);
	}
	showInfo("Info", message, cb);
}

export function dismissAlert() {
	state.select(["appState", "alerts"]).set([]);
}

export function showInfo(title, message, cb) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			id: Date.now() + Math.random(),
			type: "info",
			title,
			message,
			callback: cb
		});

		if (isMobile()) {
			postMessage({
				method: "showAlert",
				title: title,
				message: message
			});
		}
	} else {
		alert(message);
		if (cb) {
			cb();
		}
	}
}

export function showError(title, message, cb, options = {}) {
	if (globals.config.useFancyAlerts) {
		const alert = {
			id: Date.now() + Math.random(),
			type: "danger",
			title,
			message,
			callback: cb
		};
		if (_.isPlainObject(options)) {
			_.assign(alert, options);
		}
		state.select(["appState", "alerts"]).push(alert);

		if (isMobile()) {
			postMessage({
				method: "showAlert",
				title: title,
				message: message
			});
		}
	} else {
		alert(message);
		if (cb) {
			cb();
		}
	}
}

export function showWarning(title, message, cb) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			id: Date.now() + Math.random(),
			type: "warning",
			title,
			message,
			callback: cb
		});

		if (isMobile()) {
			postMessage({
				method: "showAlert",
				title: title,
				message: message
			});
		}
	} else {
		alert(message);
		if (cb) {
			cb();
		}
	}
}

export function showSuccess(title, message, cb) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			id: Date.now() + Math.random(),
			type: "success",
			title,
			message,
			callback: cb
		});
	} else {
		alert(message);
		if (cb) {
			cb();
		}
	}
}

export function showConfirm(message, title, onConfirm) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			message,
			title,
			onConfirm
		});
	} else {
		if (window.confirm(message)) {
			onConfirm();
		}
	}
}

export function showYesNo(message, title, onYesNo) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			message,
			title,
			onYesNo
		});
	} else {
		if (window.confirm(message)) {
			onYesNo();
		}
	}
}

function markErrorAlertShown(err) {
	if (_.isObject(err)) {
		err.alertShown = true;
	}
}

function getErrorDebugDetails(err) {
	if (!_.isObject(err)) {
		return [];
	}

	const details = [];
	const code = `${_.get(err, "code", "")}`.trim();
	const status = _.get(err, "status") || _.get(err, "statusCode");
	const operation = `${_.get(err, "s3Operation", "")}`.trim();
	const artifact = `${_.get(err, "saveArtifactLabel", "")}`.trim();
	const key = `${_.get(err, "s3Key", "")}`.trim();
	const requestId = `${_.get(err, "requestId") || _.get(err, "s3RequestId") || ""}`.trim();
	const extendedRequestId = `${_.get(err, "extendedRequestId") || ""}`.trim();

	if (code) {
		details.push(`Code: ${code}`);
	}
	if (status) {
		details.push(`Status: ${status}`);
	}
	if (operation) {
		details.push(`Operation: ${operation}`);
	}
	if (artifact) {
		details.push(`Target: ${artifact}`);
	}
	if (key) {
		details.push(`Path: ${key}`);
	}
	if (requestId) {
		details.push(`Request ID: ${requestId}`);
	}
	if (extendedRequestId) {
		details.push(`Extended Request ID: ${extendedRequestId}`);
	}

	return details;
}

function isS3BadDigestError(err) {
	if (!_.isObject(err)) {
		return false;
	}

	const code = `${_.get(err, "code", "")}`.trim();
	const message = `${_.get(err, "message", "")}`.trim();
	return code === "BadDigest" || message.includes("Content-MD5") || message.includes("BadDigest");
}

function pickErrorDiagnosticFields(err) {
	if (!_.isObject(err)) {
		return {
			message: `${err}`.trim()
		};
	}

	return {
		name: _.get(err, "name", ""),
		code: _.get(err, "code", ""),
		message: _.get(err, "message", ""),
		status: _.get(err, "status") || _.get(err, "statusCode") || "",
		requestId: _.get(err, "requestId") || _.get(err, "s3RequestId") || "",
		extendedRequestId: _.get(err, "extendedRequestId") || "",
		cfId: _.get(err, "cfId") || "",
		region: _.get(err, "region") || "",
		retryable: _.get(err, "retryable"),
		retryAttempt: _.get(err, "retryAttempt", 0),
		time: _.get(err, "time") ? `${_.get(err, "time")}` : ""
	};
}

function buildS3BadDigestDiagnosticContext(title, err) {
	const currentPath = typeof window !== "undefined" && window.location
		? `${window.location.pathname || ""}${window.location.search || ""}${window.location.hash || ""}`
		: "";
	return {
		diagnosticType: "authoringSaveS3BadDigest",
		alertTitle: title,
		capturedAt: new Date().toISOString(),
		error: pickErrorDiagnosticFields(err),
		save: {
			operation: _.get(err, "s3Operation", ""),
			bucket: _.get(err, "s3Bucket", ""),
			key: _.get(err, "s3Key", ""),
			artifact: _.get(err, "saveArtifactLabel", ""),
			activityId: _.get(err, "activityId", ""),
			activityName: _.get(err, "activityName", ""),
			fileName: _.get(err, "fileName", ""),
			bodyType: _.get(err, "bodyType", ""),
			bodyLength: _.get(err, "bodyLength", ""),
			bodyByteLength: _.get(err, "bodyByteLength", ""),
			fileCount: _.get(err, "fileCount", "")
		},
		client: {
			onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
			userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
			language: typeof navigator !== "undefined" ? navigator.language : "",
			currentPath
		}
	};
}

function buildS3BadDigestMeta(context = {}) {
	const save = context.save || {};
	const error = context.error || {};
	return {
		diagnosticReason: "authoringSaveS3BadDigest",
		activityId: save.activityId || "",
		activityName: save.activityName || "",
		s3Operation: save.operation || "",
		s3Bucket: save.bucket || "",
		s3Key: save.key || "",
		saveArtifactLabel: save.artifact || "",
		s3ErrorCode: error.code || "",
		s3RequestId: error.requestId || "",
		s3ExtendedRequestId: error.extendedRequestId || ""
	};
}

function sendS3BadDigestDiagnosticReport(title, context = {}) {
	recordWebDiagnosticEvent("authoringSaveS3BadDigestReportRequested", context);
	return sendWebDiagnosticReport("authoringSaveS3BadDigest", {
		diagnosticType: "authoringSaveS3BadDigest",
		source: "ambifi-cloud-authoring-save-alert",
		context,
		meta: buildS3BadDigestMeta(context),
		userIssue: {
			category: "stuck",
			severity: "annoying",
			description: `${title}: AmbiFi authoring save failed because S3 rejected the upload with a BadDigest checksum mismatch.`,
			includeDiagnostics: true,
			source: "ambifi-cloud-authoring-save-alert"
		}
	}).then((res) => {
		const storedKey = _.get(res, ["body", "storedKey"], "") || _.get(res, ["body", "key"], "") || _.get(res, ["body", "reportKey"], "");
		if (storedKey) {
			toast.success(`Diagnostic report sent to AmbiFi: ${storedKey}`, {
				position: "top-right",
				autoClose: 8000
			});
		} else {
			toast.warn("Diagnostic report was accepted, but no Admin Logs key was returned. Refresh Web Diagnostics and check again.", {
				position: "top-right",
				autoClose: 10000
			});
		}
	}).catch((sendErr) => {
		toast.error(`Unable to send diagnostic report. ${sendErr && sendErr.message ? sendErr.message : sendErr}`, {
			position: "top-right",
			autoClose: false
		});
	});
}

function getS3BadDigestAlertOptions(title, context) {
	return {
		messageMaxLength: 1600,
		actions: [
			{
				label: "Send Report to AmbiFi",
				bsStyle: "success",
				onClick: () => sendS3BadDigestDiagnosticReport(title, context)
			},
			{
				label: "Open Diagnostics",
				bsStyle: "primary",
				onClick: () => openWebDiagnostics("authoringSaveS3BadDigest", {
					diagnosticType: "authoringSaveS3BadDigest",
					source: "ambifi-cloud-authoring-save-alert",
					context,
					meta: buildS3BadDigestMeta(context),
					userIssue: {
						category: "stuck",
						severity: "annoying",
						description: `${title}: AmbiFi authoring save failed because S3 rejected the upload with a BadDigest checksum mismatch.`,
						includeDiagnostics: true,
						source: "ambifi-cloud-authoring-save-alert"
					}
				})
			},
			{
				label: "OK",
				bsStyle: "default"
			}
		]
	};
}

export function getErrorDisplayMessage(err) {
	if (_.isNil(err)) {
		return "Unknown error.";
	}

	if (_.isString(err)) {
		return err;
	}

	if (!_.isObject(err)) {
		const fallback = `${err}`.trim();
		return fallback || "Unknown error.";
	}

	if (err.code === "NetworkingError") {
		return "Network error. Please check connectivity and try again.";
	}

	if (isS3BadDigestError(err)) {
		const messageLines = [
			"The save upload was rejected by S3 because the request checksum did not match the content received.",
			"This is usually caused by a browser extension, proxy, VPN, antivirus product, or unstable network modifying the upload in transit.",
			"Retry in an incognito window or another browser. If it persists, disable VPN / proxy / security extensions and try again.",
			"Use Send Report to send AmbiFi a sanitized diagnostic snapshot with save metadata and browser/network details, not the activity JSON content."
		];
		const debugDetails = getErrorDebugDetails(err);
		if (debugDetails.length > 0) {
			messageLines.push(debugDetails.join("\n"));
		}
		return messageLines.join("\n\n");
	}

	const primaryMessage =
		`${_.get(err, "response.body.message") || _.get(err, "response.body.err.message") || _.get(err, "message") || ""}`.trim() ||
		`${err}`.trim();
	const debugDetails = getErrorDebugDetails(err);

	if (debugDetails.length > 0) {
		return `${primaryMessage || "Unknown error."}\n\n${debugDetails.join("\n")}`;
	}

	return primaryMessage || "Unknown error.";
}

export function showError2(title, err) {
	if (arguments.length < 2) {
		const message = getErrorDisplayMessage(title);
		if (isS3BadDigestError(title)) {
			const context = buildS3BadDigestDiagnosticContext("Error", title);
			recordWebDiagnosticEvent("authoringSaveS3BadDigestAlertShown", context);
			showError("Error", message, null, getS3BadDigestAlertOptions("Error", context));
			return;
		}
		showError("Error", message);
		return;
	}

	markErrorAlertShown(err);
	const message = getErrorDisplayMessage(err);
	if (isS3BadDigestError(err)) {
		const context = buildS3BadDigestDiagnosticContext(title, err);
		recordWebDiagnosticEvent("authoringSaveS3BadDigestAlertShown", context);
		showError(title, message, null, getS3BadDigestAlertOptions(title, context));
		return;
	}
	showError(title, message);
}

export function showSuccessToast(message, options = null) {

	let _options = {
		position: "top-right",
		autoClose: 3000,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	};
	if (options) {
		_options = _.assign({}, _options, options);
	}

	toast.success(message, _options);
}

export function showErrorToast(message, options = null) {

	let _options = {
		position: "top-right",
		autoClose: false,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	};
	if (options) {
		_options = _.assign({}, _options, options);
	}

	toast.error(message, _options);
}

export function showWarningToast(message, options = null) {

	let _options = {
		position: "top-right",
		autoClose: false,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	};
	if (options) {
		_options = _.assign({}, _options, options);
	}

	toast.warn(message, _options);
}


export function showInfoToast(message, options = null) {

	let _options = {
		position: "top-right",
		autoClose: false,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	};
	if (options) {
		_options = _.assign({}, _options, options);
	}

	toast.warn(message, _options);
}

export function showSaveActivityError(e) {
	if (_.get(e, "alertShown")) {
		return;
	}

	const detailMessage = getErrorDisplayMessage(e);
	showError("Save Activity", "There was an issue saving the activity. Sorry for the inconvenience, but you will need to refresh the app before you will be able to save. If you did a lot of work since your last save, you will have the opportunity to restore any lost changes." + (detailMessage ? `\n\nError details: ${detailMessage}` : ""));
}
